import {Suspense, useEffect} from 'react';
import {Outlet} from 'react-router-dom';
import {Box} from '@mui/system';
import Loader from '../../../components/common/Loader';
import {getAdminOrgDisciplesApi, getAllDisciples} from '../../../api';
import useAuthStore from '../../../store/authStore';
import useAdminStore from '../../../store/adminStore';
import AdminMobileRestrictedOverlay from '../../../components/common/AdminMobileRestrictedOverlay';
import useAdminMobileRestriction from '../../../hooks/useAdminMobileRestriction';
import {AdminLayoutProvider, useAdminLayoutContext} from './AdminLayoutContext';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';

const AdminLayoutInner = () => {
  const {config} = useAdminLayoutContext();
  const {user, selectedOrganization, isSuperAdminMasquerading} = useAuthStore(state => state);
  const {totalDisciples, setTotalDisciples} = useAdminStore(state => state);
  const {isAdminMobileRestricted, message} = useAdminMobileRestriction();

  const getDiscipleDataHandler = async () => {
    try {
      let res;
      if (user.user_role === 'SUPERADMIN') {
        res = await getAllDisciples();
        if (res?.data?.status === 200) {
          setTotalDisciples(res?.data?.data?.disciples);
        }
      } else {
        res = await getAdminOrgDisciplesApi(selectedOrganization.id, 'disciple', false, true);
        if (res?.data?.status === 200) {
          setTotalDisciples(res?.data?.data?.lifetime_disciples_count_from_groups);
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    getDiscipleDataHandler();
  }, [selectedOrganization]);

  useEffect(() => {
    if (user?.isSuperAdminMasquerading || isSuperAdminMasquerading) {
      document.body.classList.add('admin-masquerade-readonly');
    } else {
      document.body.classList.remove('admin-masquerade-readonly');
    }
    return () => document.body.classList.remove('admin-masquerade-readonly');
  }, [user, isSuperAdminMasquerading]);

  return (
    <Box display="flex" height="100vh" overflow="hidden">
      <Sidebar
        totalDisciples={totalDisciples || 0}
        hasUnsavedChanges={config.hasUnsavedChanges}
        onSidebarNavAttempt={config.onSidebarNavAttempt}
      />
      <Box
        flexGrow={1}
        sx={{
          width: 'calc(100vw - 300px)',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh'
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#fff'
          }}
        >
          <DashboardHeader
            user={user}
            heading={config.heading}
            isAddingUser={config.isAddingUser}
            handleAddClick={config.handleAddClick}
            isSearch={config.isSearch}
            searchHandler={config.searchHandler}
            isAddingGroup={config.isAddingGroup}
            isAddingDisciple={config.isAddingDisciple}
            isAddingOrganisation={config.isAddingOrganisation}
            isAddingCampaign={config.isAddingCampaign}
            isAddingDraftDay={config.isAddingDraftDay}
          />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            padding: 2,
            backgroundColor: '#f9f9f9'
          }}
        >
          <AdminMobileRestrictedOverlay isRestricted={isAdminMobileRestricted} message={message}>
            <Box sx={{flexGrow: 1, overflowY: 'auto', backgroundColor: '#f9f9f9'}}>
              {/* Suspense boundary scoped to the content area so the sidebar/header never unmount during lazy-page loads */}
              <Suspense fallback={<Loader color="#9e0001" />}>
                <Outlet />
              </Suspense>
            </Box>
          </AdminMobileRestrictedOverlay>
        </Box>
      </Box>
    </Box>
  );
};

const AdminLayout = () => (
  <AdminLayoutProvider>
    <AdminLayoutInner />
  </AdminLayoutProvider>
);

export default AdminLayout;
